import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import './ChunkModal.css';

/**
 * ChunkModal - Modal for viewing, editing, and managing document chunks
 * 
 * Features:
 * - Paginated chunk list (10 per page)
 * - View/edit chunk text
 * - Mark as information chunk
 * - Add manual chunks
 * - Q/A management per chunk (user and system generated)
 * - Status indicators
 */
const ChunkModal = ({
  isOpen,
  onClose,
  graphId,
  onChunksUpdated,
  mode = 'view', // 'view', 'edit', 'add'
}) => {
  // State
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Selected chunk for editing
  const [selectedChunk, setSelectedChunk] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [isInfoChunk, setIsInfoChunk] = useState(false);
  
  // Q/A state
  const [showQA, setShowQA] = useState(false);
  const [qaList, setQAList] = useState([]);
  const [qaLoading, setQALoading] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [editingQA, setEditingQA] = useState(null);
  
  // Add chunk dialog
  const [showAddChunk, setShowAddChunk] = useState(false);
  const [newChunkText, setNewChunkText] = useState('');
  const [newChunkTitle, setNewChunkTitle] = useState('');
  const [newChunkIsInfo, setNewChunkIsInfo] = useState(false);

  // Stats
  const [stats, setStats] = useState({ pending: 0, processed: 0, failed: 0 });

  // Load chunks
  const loadChunks = useCallback(async () => {
    if (!graphId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.getGraphChunks(graphId, page, pageSize);
      if (result.success) {
        setChunks(result.chunks || []);
        setTotalCount(result.pagination?.total_count || 0);
        setTotalPages(result.pagination?.total_pages || 0);
        setStats(result.stats || { pending: 0, processed: 0, failed: 0 });
      } else {
        setError(result.error || 'Failed to load chunks');
      }
    } catch (err) {
      console.error('Error loading chunks:', err);
      setError('Failed to load chunks');
    } finally {
      setLoading(false);
    }
  }, [graphId, page, pageSize]);

  // Load Q/A for selected chunk
  const loadQA = useCallback(async (chunkId) => {
    if (!chunkId) return;
    
    setQALoading(true);
    try {
      const result = await apiService.getChunkQA(chunkId);
      if (result.success) {
        setQAList(result.qa || []);
      }
    } catch (err) {
      console.error('Error loading Q/A:', err);
    } finally {
      setQALoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen && graphId) {
      loadChunks();
    }
  }, [isOpen, graphId, loadChunks]);

  // Load Q/A when chunk selected
  useEffect(() => {
    if (selectedChunk && showQA) {
      loadQA(selectedChunk.chunk_id);
    }
  }, [selectedChunk, showQA, loadQA]);

  // Select a chunk
  const handleSelectChunk = (chunk) => {
    setSelectedChunk(chunk);
    setEditedText(chunk.text || '');
    setEditedTitle(chunk.title || '');
    setIsInfoChunk(chunk.is_information_chunk || false);
    setEditMode(false);
    setShowQA(false);
    setQAList([]);
  };

  // Save chunk edits
  const handleSaveChunk = async () => {
    if (!selectedChunk) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const result = await apiService.updateChunk(selectedChunk.chunk_id, {
        text: editedText,
        title: editedTitle || null,
        is_information_chunk: isInfoChunk,
      });
      
      if (result.success) {
        setSuccess('Chunk updated successfully');
        setEditMode(false);
        loadChunks();
        if (onChunksUpdated) onChunksUpdated();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to update chunk');
      }
    } catch (err) {
      console.error('Error updating chunk:', err);
      setError('Failed to update chunk');
    } finally {
      setSaving(false);
    }
  };

  // Delete a chunk
  const handleDeleteChunk = async (chunkId) => {
    if (!window.confirm('Are you sure you want to delete this chunk?')) return;
    
    setSaving(true);
    try {
      const result = await apiService.deleteChunk(chunkId);
      if (result.success) {
        setSuccess('Chunk deleted');
        setSelectedChunk(null);
        loadChunks();
        if (onChunksUpdated) onChunksUpdated();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to delete chunk');
      }
    } catch (err) {
      setError('Failed to delete chunk');
    } finally {
      setSaving(false);
    }
  };

  // Add a new chunk
  const handleAddChunk = async () => {
    if (!newChunkText.trim()) {
      setError('Chunk text is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const result = await apiService.addManualChunk(graphId, {
        text: newChunkText.trim(),
        title: newChunkTitle.trim() || null,
        is_information_chunk: newChunkIsInfo,
      });
      
      if (result.success) {
        setSuccess('Chunk added successfully');
        setShowAddChunk(false);
        setNewChunkText('');
        setNewChunkTitle('');
        setNewChunkIsInfo(false);
        loadChunks();
        if (onChunksUpdated) onChunksUpdated();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to add chunk');
      }
    } catch (err) {
      setError('Failed to add chunk');
    } finally {
      setSaving(false);
    }
  };

  // Add Q/A
  const handleAddQA = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) {
      setError('Question and answer are required');
      return;
    }
    
    setSaving(true);
    try {
      const result = await apiService.addChunkQA(
        selectedChunk.chunk_id,
        newQuestion.trim(),
        newAnswer.trim()
      );
      
      if (result.success) {
        setNewQuestion('');
        setNewAnswer('');
        loadQA(selectedChunk.chunk_id);
        // Reload chunks to update qa_count
        loadChunks();
        // Update selected chunk's qa_count locally
        setSelectedChunk(prev => prev ? {...prev, qa_count: (prev.qa_count || 0) + 1} : null);
        setSuccess('Q/A added');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to add Q/A');
      }
    } catch (err) {
      setError('Failed to add Q/A');
    } finally {
      setSaving(false);
    }
  };

  // Delete Q/A
  const handleDeleteQA = async (qaId) => {
    if (!window.confirm('Delete this Q/A pair?')) return;
    
    try {
      const result = await apiService.deleteChunkQA(qaId);
      if (result.success) {
        loadQA(selectedChunk.chunk_id);
        // Reload chunks to update qa_count
        loadChunks();
        // Update selected chunk's qa_count locally
        setSelectedChunk(prev => prev ? {...prev, qa_count: Math.max((prev.qa_count || 0) - 1, 0)} : null);
      }
    } catch (err) {
      setError('Failed to delete Q/A');
    }
  };

  // Save edited Q/A
  const handleSaveQA = async (qa) => {
    setSaving(true);
    try {
      const result = await apiService.updateChunkQA(qa.chunk_qa_id, {
        question: qa.question,
        expected_answer: qa.expected_answer,
      });
      if (result.success) {
        setEditingQA(null);
        loadQA(selectedChunk.chunk_id);
      }
    } catch (err) {
      setError('Failed to update Q/A');
    } finally {
      setSaving(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'gray', icon: '○' },
      processing: { color: 'blue', icon: '⏳' },
      processed: { color: 'green', icon: '✓' },
      failed: { color: 'red', icon: '✕' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`chunk-status-badge chunk-status-${badge.color}`}>
        {badge.icon}
      </span>
    );
  };

  // Pagination controls
  const renderPagination = () => (
    <div className="chunk-pagination">
      <button
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={page <= 1 || loading}
        className="btn btn-sm"
      >
        Previous
      </button>
      <span className="pagination-info">
        Page {page} of {totalPages} ({totalCount} chunks)
      </span>
      <button
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={page >= totalPages || loading}
        className="btn btn-sm"
      >
        Next
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay chunk-modal-overlay" onClick={onClose}>
      <div className="modal-content chunk-modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header chunk-modal-header">
          <h2>
            {mode === 'add' ? 'Add Chunks' : 'View/Edit Chunks'}
          </h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="message error-message">
            {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="message success-message">
            {success}
          </div>
        )}

        {/* Stats Bar */}
        <div className="chunk-stats-bar">
          <div className="stat">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{totalCount}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Pending:</span>
            <span className="stat-value pending">{stats.pending_count || 0}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Processed:</span>
            <span className="stat-value processed">{stats.processed_count || 0}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Failed:</span>
            <span className="stat-value failed">{stats.failed_count || 0}</span>
          </div>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={loadChunks}
            disabled={loading}
            title="Refresh chunk list"
          >
            {loading ? '...' : '↻ Refresh'}
          </button>
          <button 
            className="btn btn-primary btn-sm add-chunk-btn"
            onClick={() => setShowAddChunk(true)}
          >
            + Add Chunk
          </button>
        </div>

        {/* Main Content */}
        <div className="chunk-modal-body">
          {/* Chunk List Panel */}
          <div className="chunk-list-panel">
            <h3>Chunks</h3>
            {loading ? (
              <div className="loading">Loading chunks...</div>
            ) : chunks.length === 0 ? (
              <div className="no-chunks">
                No chunks found. Upload a document and click "Chunk Document" or add chunks manually.
              </div>
            ) : (
              <>
                <div className="chunk-list">
                  {chunks.map((chunk) => (
                    <div
                      key={chunk.chunk_id}
                      className={`chunk-item ${selectedChunk?.chunk_id === chunk.chunk_id ? 'selected' : ''}`}
                      onClick={() => handleSelectChunk(chunk)}
                    >
                      <div className="chunk-item-header">
                        <span className="chunk-index">#{chunk.chunk_index + 1}</span>
                        {getStatusBadge(chunk.status)}
                        {chunk.is_information_chunk && (
                          <span className="info-badge">INFO</span>
                        )}
                        {chunk.is_edited && (
                          <span className="edited-badge">Edited</span>
                        )}
                      </div>
                      <div className="chunk-item-preview">
                        {chunk.title ? (
                          <strong>{chunk.title}</strong>
                        ) : (
                          chunk.text?.substring(0, 100) + (chunk.text?.length > 100 ? '...' : '')
                        )}
                      </div>
                      <div className="chunk-item-meta">
                        <span className="source">
                          {chunk.source_type === 'document' 
                            ? chunk.source_document_name || 'Document'
                            : 'Manually added'}
                        </span>
                        <span className="qa-count">
                          Q/A: {chunk.qa_count || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {renderPagination()}
              </>
            )}
          </div>

          {/* Chunk Detail Panel */}
          <div className="chunk-detail-panel">
            {selectedChunk ? (
              <>
                {/* Tabs */}
                <div className="chunk-detail-tabs">
                  <button
                    className={`tab ${!showQA ? 'active' : ''}`}
                    onClick={() => setShowQA(false)}
                  >
                    Content
                  </button>
                  <button
                    className={`tab ${showQA ? 'active' : ''}`}
                    onClick={() => setShowQA(true)}
                  >
                    Q/A ({selectedChunk.qa_count || 0})
                  </button>
                </div>

                {!showQA ? (
                  /* Content Tab */
                  <div className="chunk-content-tab">
                    {/* Metadata */}
                    <div className="chunk-metadata">
                      <div className="meta-row">
                        <span className="meta-label">Status:</span>
                        <span className={`meta-value status-${selectedChunk.status}`}>
                          {selectedChunk.status}
                          {selectedChunk.processed_at && (
                            <span className="processed-date">
                              ({new Date(selectedChunk.processed_at).toLocaleString()})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="meta-row">
                        <span className="meta-label">Source:</span>
                        <span className="meta-value">
                          {selectedChunk.source_type === 'document' 
                            ? selectedChunk.source_document_name || 'Document'
                            : 'Manually added'}
                        </span>
                      </div>
                      {selectedChunk.is_edited && (
                        <div className="meta-row">
                          <span className="edited-indicator">This chunk has been edited</span>
                        </div>
                      )}
                    </div>

                    {/* Title (for info chunks) */}
                    <div className="chunk-field">
                      <label>
                        <input
                          type="checkbox"
                          checked={isInfoChunk}
                          onChange={(e) => setIsInfoChunk(e.target.checked)}
                          disabled={!editMode}
                        />
                        Information Chunk
                      </label>
                      {isInfoChunk && (
                        <input
                          type="text"
                          placeholder="Title for information chunk"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          disabled={!editMode}
                          className="chunk-title-input"
                        />
                      )}
                    </div>

                    {/* Text Content */}
                    <div className="chunk-field">
                      <label>Content:</label>
                      <textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                        disabled={!editMode}
                        rows={15}
                        className="chunk-text-area"
                      />
                      <div className="word-count">
                        {editedText.split(/\s+/).filter(w => w).length} words
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="chunk-actions">
                      {editMode ? (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={handleSaveChunk}
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setEditMode(false);
                              setEditedText(selectedChunk.text || '');
                              setEditedTitle(selectedChunk.title || '');
                              setIsInfoChunk(selectedChunk.is_information_chunk || false);
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={() => setEditMode(true)}
                            disabled={selectedChunk.status === 'processed'}
                          >
                            Edit Chunk
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDeleteChunk(selectedChunk.chunk_id)}
                            disabled={saving || selectedChunk.status === 'processed'}
                            title={selectedChunk.status === 'processed' ? 'Cannot delete a processed chunk' : 'Delete this chunk'}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Q/A Tab */
                  <div className="chunk-qa-tab">
                    {/* Add Q/A Form */}
                    <div className="add-qa-form">
                      <h4>Add Q/A Pair</h4>
                      <input
                        type="text"
                        placeholder="Question"
                        value={newQuestion}
                        onChange={(e) => setNewQuestion(e.target.value)}
                        className="qa-input"
                      />
                      <textarea
                        placeholder="Expected Answer"
                        value={newAnswer}
                        onChange={(e) => setNewAnswer(e.target.value)}
                        rows={3}
                        className="qa-textarea"
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAddQA}
                        disabled={saving || !newQuestion.trim() || !newAnswer.trim()}
                      >
                        Add Q/A
                      </button>
                    </div>

                    {/* Q/A List */}
                    <div className="qa-list">
                      <h4>Q/A Pairs</h4>
                      {qaLoading ? (
                        <div className="loading">Loading Q/A...</div>
                      ) : qaList.length === 0 ? (
                        <div className="no-qa">
                          No Q/A pairs yet. Add some above or process the chunk to generate system Q/A.
                        </div>
                      ) : (
                        <>
                          {/* User Q/A */}
                          {qaList.filter(q => q.source_type === 'user').length > 0 && (
                            <div className="qa-section">
                              <h5>User Q/A</h5>
                              {qaList.filter(q => q.source_type === 'user').map((qa) => (
                                <div key={qa.chunk_qa_id} className="qa-item user-qa">
                                  {editingQA?.chunk_qa_id === qa.chunk_qa_id ? (
                                    /* Edit Mode */
                                    <div className="qa-edit-form">
                                      <input
                                        type="text"
                                        value={editingQA.question}
                                        onChange={(e) => setEditingQA({
                                          ...editingQA,
                                          question: e.target.value
                                        })}
                                        className="qa-input"
                                      />
                                      <textarea
                                        value={editingQA.expected_answer}
                                        onChange={(e) => setEditingQA({
                                          ...editingQA,
                                          expected_answer: e.target.value
                                        })}
                                        rows={2}
                                        className="qa-textarea"
                                      />
                                      <div className="qa-edit-actions">
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => handleSaveQA(editingQA)}
                                        >
                                          Save
                                        </button>
                                        <button
                                          className="btn btn-sm"
                                          onClick={() => setEditingQA(null)}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    /* View Mode */
                                    <>
                                      <div className="qa-question">
                                        <strong>Q:</strong> {qa.question}
                                      </div>
                                      <div className="qa-answer">
                                        <strong>A:</strong> {qa.expected_answer}
                                      </div>
                                      <div className="qa-actions">
                                        <button
                                          className="btn-icon"
                                          onClick={() => setEditingQA({ ...qa })}
                                          title="Edit"
                                        >
                                          ✎
                                        </button>
                                        <button
                                          className="btn-icon delete"
                                          onClick={() => handleDeleteQA(qa.chunk_qa_id)}
                                          title="Delete"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* System Q/A */}
                          {qaList.filter(q => q.source_type === 'system').length > 0 && (
                            <div className="qa-section">
                              <h5>System Generated Q/A</h5>
                              {qaList.filter(q => q.source_type === 'system').map((qa) => (
                                <div key={qa.chunk_qa_id} className="qa-item system-qa">
                                  <div className="qa-question">
                                    <strong>Q:</strong> {qa.question}
                                  </div>
                                  <div className="qa-answer">
                                    <strong>A:</strong> {qa.expected_answer}
                                  </div>
                                  {qa.graph_answer && (
                                    <div className={`qa-result ${qa.pass ? 'pass' : 'fail'}`}>
                                      <strong>Graph Answer:</strong> {qa.graph_answer}
                                      <span className="result-badge">
                                        {qa.pass ? '✓ Pass' : '✕ Fail'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-selection">
                Select a chunk from the list to view or edit
              </div>
            )}
          </div>
        </div>

        {/* Add Chunk Dialog */}
        {showAddChunk && (
          <div className="add-chunk-dialog">
            <div className="dialog-content">
              <h3>Add New Chunk</h3>
              <div className="dialog-field">
                <label>
                  <input
                    type="checkbox"
                    checked={newChunkIsInfo}
                    onChange={(e) => setNewChunkIsInfo(e.target.checked)}
                  />
                  Information Chunk
                </label>
              </div>
              {newChunkIsInfo && (
                <div className="dialog-field">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={newChunkTitle}
                    onChange={(e) => setNewChunkTitle(e.target.value)}
                    placeholder="Title for information chunk"
                  />
                </div>
              )}
              <div className="dialog-field">
                <label>Content:</label>
                <textarea
                  value={newChunkText}
                  onChange={(e) => setNewChunkText(e.target.value)}
                  placeholder="Enter chunk text..."
                  rows={10}
                />
              </div>
              <div className="dialog-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleAddChunk}
                  disabled={saving || !newChunkText.trim()}
                >
                  {saving ? 'Adding...' : 'Add Chunk'}
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    setShowAddChunk(false);
                    setNewChunkText('');
                    setNewChunkTitle('');
                    setNewChunkIsInfo(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="modal-footer chunk-modal-footer">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ChunkModal;
